<template>
  <v-dialog v-model="showDialog" width="500" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("transactions.addTitle") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="validating || creating"
          @click="closeDialog()"
        ></v-btn>
      </v-card-title>

      <v-form
        v-if="step === 'form'"
        ref="form"
        :disabled="validating || loadingBeneficiaries"
        validate-on="invalid-input lazy"
        @submit.prevent="validatePayout"
      >
        <v-card-text>
          <v-autocomplete
            v-model="beneficiaryId"
            :items="beneficiaryItems"
            :rules="[required]"
            :loading="loadingBeneficiaries"
            :no-data-text="t('beneficiaries.noBeneficiaries')"
            variant="outlined"
            class="mb-2"
            item-title="title"
            item-value="value"
            clearable
          >
            <template #label>
              {{ t("transfers.beneficiaryName") }}
              <span class="text-error">*</span>
            </template>
          </v-autocomplete>
          <v-text-field
            v-model.number="amount"
            :rules="[required, positiveNumber, maxTwoDecimals]"
            type="number"
            step="0.01"
            min="0"
            variant="outlined"
            class="mb-2"
          >
            <template #label>
              {{ t("common.amount") }}
              <span class="text-error">*</span>
            </template>
            <template #append-inner>{{ currencySymbol }}</template>
          </v-text-field>
          <v-text-field
            v-model.trim="label"
            :rules="[maxLength(140), validSepaCharset]"
            variant="outlined"
            counter="140"
            maxlength="140"
          >
            <template #label>
              {{ t("transactions.details.label") }}
            </template>
          </v-text-field>
          <v-text-field
            v-model.trim="reference"
            :rules="[maxLength(35), validSepaCharset]"
            variant="outlined"
            counter="35"
            maxlength="35"
          >
            <template #label>
              {{ t("transactions.details.reference") }}
            </template>
          </v-text-field>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn :disabled="validating" @click="closeDialog()">{{ t("common.cancel") }}</v-btn>
          <v-btn
            type="submit"
            :loading="validating"
            :disabled="validating || loadingBeneficiaries"
            color="primary"
            variant="flat"
            >{{ t("beneficiaries.validate") }}</v-btn
          >
        </v-card-actions>
      </v-form>

      <template v-else-if="validation && selectedBeneficiary">
        <v-card-text>
          <div class="mb-4">
            <div class="text-sm text-medium-emphasis">{{ t("transfers.beneficiaryName") }}</div>
            <div>{{ selectedBeneficiary.name }}</div>
            <div class="text-sm text-medium-emphasis mt-2">{{ t("common.iban") }}</div>
            <div>{{ selectedBeneficiary.metadata?.iban }}</div>
            <div class="text-sm text-medium-emphasis mt-2">{{ t("common.amount") }}</div>
            <div>{{ formattedAmount }}</div>
            <template v-if="label">
              <div class="text-sm text-medium-emphasis mt-2">
                {{ t("transactions.details.label") }}
              </div>
              <div>{{ label }}</div>
            </template>
            <template v-if="reference">
              <div class="text-sm text-medium-emphasis mt-2">
                {{ t("transactions.details.reference") }}
              </div>
              <div>{{ reference }}</div>
            </template>
          </div>

          <div class="text-sm text-medium-emphasis mb-2">
            {{ t("transactions.details.verificationOfPayee.title") }}
          </div>
          <v-chip
            :prepend-icon="validationIcon"
            :color="validationColor"
            variant="tonal"
            class="mb-3"
          >
            {{ validationLabel }}
          </v-chip>
          <div class="text-sm text-medium-emphasis">{{ validationMessage }}</div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn :disabled="creating" @click="backToForm()">{{ t("beneficiaries.back") }}</v-btn>
          <v-btn
            :loading="creating"
            :disabled="creating"
            color="primary"
            variant="flat"
            @click="confirmTransfer()"
            >{{ t("common.send") }}</v-btn
          >
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useAccountCurrency } from "@/composables/useAccountCurrency";
import { useBankAccount } from "@/composables/useBankAccount";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import { formatCurrency } from "@/utils/tools";
import { startAuthentication } from "@/utils/webAuthn";
import type { VerificationOfPayee } from "@aircap/common/transaction";
import {
  treezorBeneficiariesFullPath,
  type BeneficiaryValidation,
  type Beneficiary as TreezorBeneficiary,
} from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { collection, orderBy, query, where } from "firebase/firestore";
import { computed, ref, toRef, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  modelValue: boolean;
  accountId: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t, locale } = useI18n();
const { required, maxLength, positiveNumber } = useRules();
const { showSuccessToast, showErrorToast } = useToast();
const accountId = toRef(props, "accountId");
const { currencyCode, currencySymbol } = useAccountCurrency(accountId);
const { account } = useBankAccount(accountId);

const form = ref<VForm>();
const beneficiaryId = ref<string>();
const amount = ref<number | null>(null);
const label = ref("");
const reference = ref("");
const step = ref<"form" | "validation">("form");
const validation = ref<BeneficiaryValidation>();
const validating = ref(false);
const creating = ref(false);

const maxTwoDecimals = (value: unknown) => {
  if (!value && value !== 0) return true;
  if (typeof value !== "number" && typeof value !== "string") return t("errors.max2digits");
  return /^\d+(?:\.\d{1,2})?$/.test(value.toString()) || t("errors.max2digits");
};

const validSepaCharset = (value: unknown) => {
  if (!value) return true;
  if (typeof value !== "string") return t("errors.invalidSepaCharset");
  if (value.startsWith("/") || value.endsWith("/") || value.includes("//")) {
    return t("errors.invalidSepaCharset");
  }
  return /^[a-zA-Z0-9 /?:().,'+-]*$/.test(value) || t("errors.invalidSepaCharset");
};

const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const beneficiariesSource = computed(() =>
  query(
    collection<TreezorBeneficiary>(db, treezorBeneficiariesFullPath(accountId.value)),
    where<TreezorBeneficiary>("isActive", "==", true),
    orderBy<TreezorBeneficiary>("createdDate", "desc"),
  ),
);
const { data: beneficiaries, pending: loadingBeneficiaries } =
  useCollection<Omit<TreezorBeneficiary, "id">>(beneficiariesSource);

const beneficiaryItems = computed(() =>
  beneficiaries.value
    .filter(
      (
        beneficiary,
      ): beneficiary is Omit<TreezorBeneficiary, "id"> & {
        id: string;
        metadata: NonNullable<Required<TreezorBeneficiary["metadata"]>>;
      } => !!beneficiary.metadata?.iban,
    )
    .map((beneficiary) => ({
      title: `${beneficiary.name} (${beneficiary.metadata.iban})`,
      value: beneficiary.id,
    })),
);

const selectedBeneficiary = computed(() =>
  beneficiaries.value.find((beneficiary) => beneficiary.id === beneficiaryId.value),
);

const requiresTransferRequest = computed(() => {
  const threshold = account.value?.transferAmountThreshold;
  if (amount.value === null || threshold === undefined || threshold === null) return false;
  return amount.value >= threshold;
});

const formattedAmount = computed(() => {
  if (amount.value === null || !currencyCode.value) return "";
  return formatCurrency({
    value: amount.value,
    currencyCode: currencyCode.value,
    locale: locale.value,
  });
});

const toVerificationOfPayee = (
  result: BeneficiaryValidation["vop"]["result"],
): VerificationOfPayee => {
  switch (result) {
    case "MTCH":
      return "Match";
    case "CMTC":
      return "CloseMatch";
    case "NMTC":
      return "NoMatch";
    case "NOAP":
      return "VerificationNotPossible";
  }
};

const validationLabel = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "";
  switch (result) {
    case "MTCH":
      return t("transactions.details.verificationOfPayee.Match");
    case "CMTC":
      return t("transactions.details.verificationOfPayee.CloseMatch");
    case "NMTC":
      return t("transactions.details.verificationOfPayee.NoMatch");
    case "NOAP":
      return t("transactions.details.verificationOfPayee.VerificationNotPossible");
  }
});

const validationColor = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "info";
  switch (result) {
    case "MTCH":
      return "success";
    case "CMTC":
      return "warning";
    case "NMTC":
      return "error";
    case "NOAP":
      return "warning";
  }
});

const validationIcon = computed(() => {
  const result = validation.value?.vop.result;
  if (!result) return "mdi-shield-alert";
  switch (result) {
    case "MTCH":
      return "mdi-shield-check";
    case "CMTC":
      return "mdi-shield-alert";
    case "NMTC":
      return "mdi-alert-circle";
    case "NOAP":
      return "mdi-shield-alert";
  }
});

const validationMessage = computed(() => {
  const vop = validation.value?.vop;
  if (!vop) return "";
  switch (vop.result) {
    case "MTCH":
      return t("beneficiaries.validation.match");
    case "CMTC":
      return t("beneficiaries.validation.closeMatch", {
        matchedName: vop.matchedName || t("common.none"),
      });
    case "NMTC":
      return t("beneficiaries.validation.noMatch");
    case "NOAP":
      return vop.reasonCode === "9009"
        ? t("beneficiaries.validation.unavailable")
        : t("beneficiaries.validation.notPossible");
  }
});

watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue) {
      beneficiaryId.value = undefined;
      amount.value = null;
      label.value = "";
      reference.value = "";
      step.value = "form";
      validation.value = undefined;
      form.value?.resetValidation();
    }
  },
);

const closeDialog = () => {
  showDialog.value = false;
};

const backToForm = () => {
  step.value = "form";
  validation.value = undefined;
};

const validatePayout = async () => {
  if (!form.value) return;
  const { valid } = await form.value.validate();
  if (!valid) return;

  const beneficiary = selectedBeneficiary.value;
  if (!beneficiary?.metadata?.iban) {
    showErrorToast();
    return;
  }

  validating.value = true;
  try {
    validation.value = await call("validateTreezorBeneficiary")({
      name: beneficiary.name,
      iban: beneficiary.metadata.iban,
    });
    step.value = "validation";
  } catch (cause) {
    const error = new Error("Error validating beneficiary for payout", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    validating.value = false;
  }
};

const createTransferRequest = async () => {
  const beneficiary = selectedBeneficiary.value;
  if (!validation.value || amount.value === null || !beneficiary?.metadata?.iban) return;

  await call("createTransferRequest")({
    accountId: props.accountId,
    beneficiaryName: beneficiary.name,
    beneficiaryIban: beneficiary.metadata.iban,
    amount: amount.value,
    ...(label.value ? { label: label.value } : {}),
    ...(reference.value ? { reference: reference.value } : {}),
    verificationOfPayee: toVerificationOfPayee(validation.value.vop.result),
    // TODO: Treezor, add comment, trackedCategoryId and files
  });
};

const createPayout = async () => {
  if (!validation.value || amount.value === null || !beneficiaryId.value) return;

  const payout = {
    beneficiaryId: parseInt(beneficiaryId.value, 10),
    amount: amount.value,
    beneficiaryValidationId: validation.value.beneficiaryValidationId,
    ...(label.value ? { label: label.value } : {}),
    ...(reference.value ? { reference: reference.value } : {}),
    // TODO: Treezor, add comment, trackedCategoryId and files
  };
  const { webAuthnAuthenticationOptions } = await call("createTreezorPayoutSCAPayload")({
    accountId: props.accountId,
    beneficiaryId: payout.beneficiaryId,
    amount: payout.amount,
  });
  const webAuthnAuthenticationResponse = await startAuthentication({
    optionsJSON: webAuthnAuthenticationOptions,
  });
  await call("createTreezorPayout")({
    accountId: props.accountId,
    ...payout,
    webAuthnAuthenticationResponse,
  });
};

const confirmTransfer = async () => {
  if (!validation.value || amount.value === null || !beneficiaryId.value) return;

  creating.value = true;
  try {
    if (requiresTransferRequest.value) {
      await createTransferRequest();
    } else {
      await createPayout();
    }
    showSuccessToast();
    closeDialog();
  } catch (cause) {
    const error = new Error(
      requiresTransferRequest.value
        ? "Error creating transfer request"
        : "Error creating treezor payout",
      { cause },
    );
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
  } finally {
    creating.value = false;
  }
};
</script>
